import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * MentorCenterPage — 导师中心
 *
 * 页面结构（从上到下）：
 *   1. Hero Banner
 *   2. 认证查询区（输入框 + 查询按钮 + 结果展示）
 *   3. 推荐导师区（按认证等级排序的导师列表）
 *
 * 设计思路：导师由认证考试产生，认证查询的对象就是导师，
 * 输入认证码查询导师信息，下方推荐导师供拜师。两者合一。
 */
export default class MentorCenterPage extends IndexPage {
  oninit(vnode) {
    Page.prototype.oninit.call(this, vnode);
    this.bodyClass = 'App--index';

    // 认证查询
    this.lookupCode = '';
    this.lookupResult = null;
    this.lookupLoading = false;
    this.lookupError = null;

    // 导师数据
    this.loading = true;
    this.searchQuery = '';
    this.mentors = [];
    this.notices = [];
    this.myMentor = null;
    this.sending = null;
    this.showNoticeForm = false;
    this.noticeText = '';
    this.noticeSubmitting = false;
    this.noticeError = '';
    this.deleting = null;

    this.loadData();
  }

  hero() {
    const hidden = app.forum.attribute('taroMentorHeroHidden') || false;
    if (hidden) return null;
    const subtitle = app.forum.attribute('taroMentorHeroSubtitle') || '查询导师认证，寻找你的导师';
    const icon = app.forum.attribute('taroMentorHeroIcon') || 'fas fa-chalkboard-teacher';
    const color = app.forum.attribute('taroMentorHeroColor') || '';
    return (
      <header className="Hero" style={color ? { '--hero-bg': color } : undefined}>
        <div className="container">
          <h1 className="Hero-title">
            <i className={icon} style={{ marginRight: '8px' }} />
            导师中心
          </h1>
          {subtitle ? <div className="Hero-subtitle">{subtitle}</div> : null}
        </div>
      </header>
    );
  }

  contentItems() {
    const items = new ItemList();
    items.add('mentorContent', this.pageContent(), 100);
    return items;
  }

  pageContent() {
    return (
      <div className="MentorCenterPage">
        {/* ═══ 认证查询区 ═══ */}
        <div className="MentorCenterPage-lookupSection">
          <div className="MentorCenterPage-lookupHeader">
            <i className="fas fa-certificate" />
            <span>认证查询</span>
          </div>
          <p className="MentorCenterPage-lookupDesc">
            输入导师的认证码，查询其认证等级与资质信息
          </p>
          <div className="MentorCenterPage-lookupForm">
            <input
              className="FormControl"
              type="text"
              placeholder="请输入认证码，如 TARO-XXXXXXXX"
              value={this.lookupCode}
              oninput={(e) => { this.lookupCode = e.target.value; m.redraw(); }}
              onkeydown={(e) => { if (e.key === 'Enter') this.lookup(); }}
            />
            <Button className="Button Button--primary" loading={this.lookupLoading} onclick={() => this.lookup()}>
              <i className="fas fa-search" style={{ marginRight: '6px' }} />
              查询
            </Button>
          </div>

          {this.lookupError && (
            <div className="CertLookup-error">
              <i className="fas fa-exclamation-circle" /> {this.lookupError}
            </div>
          )}

          {this.lookupResult && (
            <div className="CertLookup-result">
              <h3><i className="fas fa-certificate" /> 认证信息</h3>
              <div className="CertLookup-resultRow">
                <span className="CertLookup-label">认证等级</span>
                <span className={'CertificationBadge CertificationBadge--' + this.lookupResult.level}>
                  <i className="fas fa-certificate" /> {this.lookupResult.level}
                </span>
              </div>
              <div className="CertLookup-resultRow">
                <span className="CertLookup-label">认证码</span>
                <code>{this.lookupResult.code}</code>
              </div>
              <div className="CertLookup-resultRow">
                <span className="CertLookup-label">有效期至</span>
                <span>{this.lookupResult.expires_at || '永久'}</span>
              </div>
              <div className="CertLookup-resultRow">
                <span className="CertLookup-label">状态</span>
                <span className={this.lookupResult.is_active ? 'CertLookup-active' : 'CertLookup-inactive'}>
                  {this.lookupResult.is_active ? '✅ 有效' : '❌ 已失效'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ═══ 推荐导师区 ═══ */}
        <div className="MentorCenterPage-mentorSection">
          <div className="MentorCenterPage-mentorSectionHeader">
            <h2>
              <i className="fas fa-users" style={{ marginRight: '8px' }} />
              推荐导师
            </h2>
            <div className="MentorCenterPage-search">
              <input
                className="FormControl"
                type="text"
                placeholder="搜索导师名称或等级..."
                value={this.searchQuery}
                oninput={e => { this.searchQuery = e.target.value; m.redraw(); }}
              />
            </div>
          </div>

          {this.loading ? <LoadingIndicator /> : (
            this.filteredMentors.length === 0 ? (
              <div className="MentorCenterPage-empty">
                <p>{this.searchQuery ? '未找到匹配的导师' : '暂无可用导师'}</p>
              </div>
            ) : (
              <div className="MentorCenterPage-mentorList">
                {this.filteredMentors.map(mentor => {
                  const isBound = this.myMentor && this.myMentor.id === mentor.id;
                  const attrs = mentor.attributes || mentor;
                  const wandLevel = attrs.taroWandLevel || 'C0';
                  const capacity = attrs.taroMentorCapacity || null;
                  const mentorNotice = this.notices.find(n => {
                    const a = n.attributes || n;
                    return String(a.mentorId) === String(mentor.id);
                  });
                  const noticeContent = mentorNotice?.attributes?.content || null;

                  return (
                    <div className={'TaroMentorList-card' + (isBound ? ' is-bound' : '')} key={mentor.id}>
                      <div className="TaroMentorList-card-avatar">{this.avatarHtml(mentor)}</div>
                      <div className="TaroMentorList-card-info">
                        <div className="TaroMentorList-card-name">{this.userName(mentor)}</div>
                        <div className="TaroMentorList-card-meta">
                          <span>认证等级 <strong style="color: #4D698E;">{wandLevel}</strong></span>
                          {capacity && (
                            <span>学员 {capacity.currentStudents}/{capacity.maxStudents}</span>
                          )}
                        </div>
                        {noticeContent && (
                          <div className="MentorCenterPage-noticeContent">
                            <i className="fas fa-bullhorn"></i> {noticeContent}
                          </div>
                        )}
                      </div>
                      <div className="TaroMentorList-card-action">
                        {isBound ? (
                          <span className="TaroMentorList-card-badge approved">已绑定</span>
                        ) : (
                          <button className="Button Button--primary" disabled={this.sending === mentor.id}
                            onclick={() => this.requestMentor(mentor)}>
                            {this.sending === mentor.id ? '发送中...' : '拜师'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // 认证查询
  // ═══════════════════════════════════════════

  async lookup() {
    const code = this.lookupCode.trim();
    if (!code) return;
    this.lookupLoading = true;
    this.lookupError = null;
    this.lookupResult = null;
    m.redraw();

    try {
      const res = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/taro/certifications/query/' + encodeURIComponent(code),
      });
      this.lookupResult = res.data.attributes;
    } catch (e) {
      this.lookupError = '认证码无效或已过期';
    } finally {
      this.lookupLoading = false;
      m.redraw();
    }
  }

  // ═══════════════════════════════════════════
  // 导师数据
  // ═══════════════════════════════════════════

  loadData() {
    this.loading = true;
    m.redraw();

    const api = app.forum.attribute('apiUrl');

    Promise.all([
      app.request({ method: 'GET', url: api + '/taro/mentors' }).then(r => {
        this.mentors = Array.isArray(r) ? r : r.data || [];
      }).catch(() => { this.mentors = []; }),

      app.request({ method: 'GET', url: api + '/taro/mentors/my' }).then(r => {
        const data = r.data || r;
        this.myMentor = (data && data.id) ? data : null;
      }).catch(() => { this.myMentor = null; }),

      app.request({ method: 'GET', url: api + '/taro/mentor/notices' }).then(r => {
        this.notices = Array.isArray(r) ? r : r.data || [];
      }).catch(() => { this.notices = []; }),
    ]).then(() => {
      this.loading = false;
      m.redraw();
    });
  }

  get filteredMentors() {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.mentors;
    return this.mentors.filter(m => {
      const name = (m.attributes?.displayName || m.attributes?.username || '').toLowerCase();
      const level = (m.attributes?.taroWandLevel || '').toLowerCase();
      return name.includes(q) || level.includes(q);
    });
  }

  requestMentor(mentor) {
    if (!app.session.user) return;
    if (!confirm('确定向 ' + this.userName(mentor) + ' 申请拜师？')) return;

    const api = app.forum.attribute('apiUrl');
    this.sending = mentor.id();
    m.redraw();

    app.request({
      method: 'POST',
      url: api + '/taro/mentors/request',
      body: { mentor_id: mentor.id(), message: '' },
    }).then(() => {
      this.sending = null;
      alert('拜师申请已发送，等待导师确认！');
      this.loadData();
    }).catch(err => {
      this.sending = null;
      alert('申请失败：' + (err.response?.errors?.[0]?.detail || err.statusText || '错误'));
      m.redraw();
    });
  }

  userName(obj) {
    const a = obj.attributes || obj || {};
    return a.displayName || a.username || '未知';
  }

  userAvatar(obj) {
    const a = obj.attributes || obj || {};
    return a.avatarUrl || null;
  }

  avatarHtml(obj, size) {
    const name = this.userName(obj);
    const avatar = this.userAvatar(obj);
    const cls = 'TaroMentorList-card-avatar-placeholder' + (size === 'lg' ? ' large' : '');
    if (avatar) {
      return <img src={avatar} alt={name} />;
    }
    return <div className={cls}>{name.charAt(0).toUpperCase()}</div>;
  }
}